import { useEffect, useMemo, useState } from 'react';
import { createFilterOptions } from '@mui/material';
import * as R from 'ramda';

import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import { SelectAutocomplete } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderField } from '@atom/types/work';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './workOrderFields.module.css';

interface Props {
  field: WorkOrderField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  fieldsLoading?: Set<string>;
  isNested?: boolean;
  customFieldView: CustomFieldView;
  width?: string;
  dataCy?: string;
  requiredFieldIds?: Set<string>;
  requiredFieldName?: keyof WorkOrderField;
  isTemplate?: boolean;
  onClick?: () => void;
  error?: boolean;
  helperText?: string;
}

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

const WorkOrderEnumSingleField = ({
  field,
  onChange,
  isDisabled,
  fieldsLoading = new Set([]),
  isNested,
  customFieldView,
  width,
  dataCy,
  requiredFieldIds = new Set([]),
  requiredFieldName = 'requiredOnCreate',
  isTemplate,
  onClick,
  error,
  helperText,
}: Props) => {
  const [value, setValue] = useState<string>(field.value);

  const handleChange = (event: any, option: string) => {
    const newValue = R.isNil(option) ? '' : option;

    setValue(newValue);
    onChange(field.id, newValue);
  };

  useEffect(() => {
    setValue(field.value);
  }, [field]);

  const nestedField = useMemo(() => {
    if (!field.subFields) {
      return null;
    }

    const nestedFieldIndex = R.findIndex(R.propEq(value, 'matchValue'))(
      field.subFields,
    );

    const subField = field.subFields[nestedFieldIndex]?.nestedField;

    // Cascading fields inherit the parent's required flags so fields saved
    // before required was stamped onto the tree still display as required.
    return subField
      ? {
          ...subField,
          required: field.required,
          requiredOnCreate: field.requiredOnCreate,
        }
      : null;
  }, [value, field]);

  const loading = field.subFieldsTruncated && fieldsLoading.has(field.id);
  const hasSubFields = !isNilOrEmpty(field.subFields);

  const fieldWidth = width ?? getTaskFieldWidth(customFieldView);

  const isCascading = hasSubFields || isNested;

  // Cascading levels and filters keep a single-line label; everywhere else
  // long labels wrap
  const isFilter = customFieldView === CustomFieldView.FILTER;

  const containerStyles = isCascading
    ? {
        marginRight: '0.5rem',
        marginBottom: '5px',
        width: '9rem',
      }
    : {
        width: fieldWidth,
      };

  const tooltipText = value ? `${field.title}: ${value}` : field.title;

  const labelColor = requiredFieldIds.has(field.id)
    ? colors.brand.red
    : colors.neutral.dim;

  // template displays asterisk on required or requiredOnCreate
  const text =
    (isTemplate && (field.required || field.requiredOnCreate)) ||
    !!field[requiredFieldName]
      ? `* ${tooltipText}`
      : tooltipText;

  return (
    <>
      <div style={containerStyles} onClick={onClick}>
        {isCascading || isFilter ? (
          <TextOverflowTooltip
            width={isCascading ? '9rem' : fieldWidth}
            text={text}
            color={labelColor}
            fontSize={fonts.sm}
          />
        ) : (
          <div className={classes.fieldTitle} style={{ color: labelColor }}>
            {text}
          </div>
        )}
        <SelectAutocomplete
          id={field.id}
          options={field.enumeration}
          value={value}
          onChange={handleChange}
          disabled={isDisabled}
          loading={loading}
          filterOptions={filterOptions}
          TextFieldProps={{ onClick }}
          error={error}
          helperText={helperText}
          data-cy={dataCy ?? `workInfoField-${field.title}-EnumSingleInput`}
        />
      </div>
      {nestedField && (
        <WorkOrderEnumSingleField
          field={nestedField}
          onChange={onChange}
          isDisabled={isDisabled}
          fieldsLoading={fieldsLoading}
          isNested
          customFieldView={customFieldView}
          requiredFieldIds={requiredFieldIds}
          requiredFieldName={requiredFieldName}
          isTemplate={isTemplate}
          onClick={onClick}
        />
      )}
    </>
  );
};

export default WorkOrderEnumSingleField;
